/**
 * Lightweight in-app notification chime.
 *
 * Why a synth tone instead of an audio asset?
 *  - No bundled .mp3/.wav file required (zero asset shipping)
 *  - Works in Capacitor WebView on Android + iOS without entitlement faff
 *  - Survives unmute/autoplay edge-cases gracefully
 *
 * The chime is a 2-tone bell — 880Hz → 1320Hz over ~280ms with a quick
 * envelope ramp so it sounds like a soft "ding-ding" rather than a beep.
 *
 * Mute model:
 *  - LocalStorage key ``bizil_sound_muted`` (set to "1" to silence)
 *  - Honoured by every caller so a single toggle suppresses all chimes
 *
 * Autoplay model:
 *  - First call may fail if no user gesture has occurred yet (desktop
 *    Chrome / iOS Safari). We catch and noop — the next interactive
 *    chime will succeed because by then the user has clicked through.
 *  - Inside the Capacitor APK WebView autoplay restrictions are relaxed
 *    so the polling path works from cold start.
 */

const MUTE_KEY = "bizil_sound_muted";

let _ctx = null;
function getCtx() {
    if (_ctx) return _ctx;
    try {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return null;
        _ctx = new Ctor();
        return _ctx;
    } catch {
        return null;
    }
}

/** Returns true when the user has explicitly silenced in-app chimes. */
export function isMuted() {
    try { return localStorage.getItem(MUTE_KEY) === "1"; }
    catch { return false; }
}

/** Persist the mute preference. ``true`` = silent, ``false`` = audible. */
export function setMuted(v) {
    try {
        if (v) localStorage.setItem(MUTE_KEY, "1");
        else localStorage.removeItem(MUTE_KEY);
    } catch { /* private mode — fail silent */ }
}

/** Play a notification chime. Safe to call from any UI handler. No-op
 * when the user has muted or when the device is in silent mode (we
 * can't read the hardware silent switch — best-effort only).
 *
 * ``opts.urgent`` — new-order alerts (a merchant/rider genuinely can't
 * afford to miss) get a longer, more insistent 3-note ascending phrase
 * repeated twice (~1.6s) instead of the routine 2-tone ding (~0.34s) —
 * audibly distinct and "rings" for a few seconds, same intent as the
 * native bundled alert sound (see fcm.py:NEW_ORDER_CHANNEL_ID /
 * scripts/download_notification_sound.py) but needs no asset for the
 * foreground case, since WebAudio synthesizes it on the fly. */
export function playNotifSound(opts = {}) {
    if (isMuted() && !opts.force) return;
    const ctx = getCtx();
    if (!ctx) return;
    // Resume the context if it was suspended by the browser's autoplay
    // policy. Catches the "first chime after load" case where iOS
    // Safari starts the context as ``suspended``.
    try { ctx.resume?.(); } catch { /* ignore */ }

    const now = ctx.currentTime;
    const blip = (freq, start, len, peak = 0.35, wave = "sine") => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = wave;
        osc.frequency.value = freq;
        // Attack-decay envelope so the tone sounds like a bell, not a beep.
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(peak, start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + len);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + len + 0.02);
    };

    if (opts.urgent) {
        // Bright ascending triad (A5 → C#6 → E6), repeated twice with a
        // short gap — louder peak gain than the routine chime, and long
        // enough to actually be noticed if the phone isn't in-hand.
        const notes = [880, 1108.73, 1318.51];
        const noteLen = 0.16;
        const gap = 0.05;
        let t = now;
        for (let rep = 0; rep < 2; rep++) {
            for (const freq of notes) {
                // ``gain``/``wave`` let the persistent order alert (alertSound.js) ring louder; defaults unchanged.
                blip(freq, t, noteLen, opts.gain ?? 0.5, opts.wave || "sine");
                t += noteLen + gap;
            }
            t += 0.15;
        }
        return;
    }
    blip(880, now, 0.18);             // first ding
    blip(1320, now + 0.14, 0.20);     // second ding (perfect 5th up)
}

/** Convenience hook for components that just need to compare a counter
 * across renders and chime on increase. Usage:
 *
 *     useChimeOnIncrease(unreadCount);
 *
 * The hook handles its own ref so the caller can pass any number. */
import { useEffect, useRef } from "react";

export function useChimeOnIncrease(value, opts = {}) {
    const prev = useRef(null);
    useEffect(() => {
        // ``null``/``undefined`` means "not loaded yet" — the caller
        // must distinguish this from a genuine, already-known zero (see
        // CustomerHeader.jsx / MarketplaceHome.jsx). Without this guard,
        // a component that starts its counter at 0 before the real
        // value arrives looks identical to "count just increased from
        // 0" on every fresh mount — and since these header components
        // fully unmount/remount on every tab switch, that produced a
        // spurious chime on every navigation whenever the user already
        // had unread items, not just when a new one actually arrived
        // (user-reported: beep on switching tabs).
        if (value == null) return;
        const v = Number(value);
        // Skip the first REAL value so we don't chime on mount when the
        // user already had unread items waiting.
        if (prev.current != null && v > prev.current) {
            playNotifSound(opts);
        }
        prev.current = v;
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
}
